import type { RequirementFrequency } from '@/api/planning/requirements';

export type PlanSlot = { days: number[] };

/** What the monthly expectation needs from a requirement. */
export type ExpectationInput = {
  frequency: RequirementFrequency;
  visitsPerPeriod: number;
  startsOn: string;
  endsOn: string | null | undefined;
  weeklyPlans: PlanSlot[];
};

export type MonthRange = { year: number; month: number };

const DAYS_PER_WEEK = 7;
const DAYS_PER_FORTNIGHT = 14;
const MS_PER_DAY = 86_400_000;

const ISO_DATE_PARTS = { yearIdx: 0, monthIdx: 1, dayIdx: 2 } as const;

function parseISODate(iso: string): Date {
  const parts = iso.split('-').map(Number);
  const y = parts[ISO_DATE_PARTS.yearIdx] ?? 0;
  const m = parts[ISO_DATE_PARTS.monthIdx] ?? 1;
  const d = parts[ISO_DATE_PARTS.dayIdx] ?? 1;
  return new Date(Date.UTC(y, m - 1, d));
}

function monthBounds({ year, month }: MonthRange): { monthStart: Date; monthEnd: Date } {
  return {
    monthStart: new Date(Date.UTC(year, month - 1, 1)),
    monthEnd: new Date(Date.UTC(year, month, 0)),
  };
}

function countPlansMatching(plans: PlanSlot[], wday: number): number {
  return plans.reduce((acc, p) => acc + (p.days.includes(wday) ? 1 : 0), 0);
}

function effectiveRange(
  startsOn: string,
  endsOn: string | null | undefined,
  range: MonthRange,
): { from: Date; to: Date } {
  const { monthStart, monthEnd } = monthBounds(range);
  const endDate = endsOn ? parseISODate(endsOn) : monthEnd;
  return {
    from: new Date(Math.max(monthStart.getTime(), parseISODate(startsOn).getTime())),
    to: new Date(Math.min(monthEnd.getTime(), endDate.getTime())),
  };
}

function plannedVisitsBetween(plans: PlanSlot[], from: Date, to: Date): number {
  let total = 0;
  const day = new Date(from);
  while (day <= to) {
    total += countPlansMatching(plans, day.getUTCDay());
    day.setUTCDate(day.getUTCDate() + 1);
  }
  return total;
}

// A monthly period is the calendar month being measured.
function periodDays(frequency: RequirementFrequency, range: MonthRange): number {
  if (frequency === 'weekly') return DAYS_PER_WEEK;
  if (frequency === 'biweekly') return DAYS_PER_FORTNIGHT;
  return monthBounds(range).monthEnd.getUTCDate();
}

/**
 * Visits expected in a month for one requirement, unrounded. Weekly requirements count
 * their weekly-plan days inside the active range; biweekly and monthly ones carry no plans
 * and prorate visitsPerPeriod over their active days. Matches server-side
 * MonthlyExpectationCalculator, which sums a patient's requirements per career before
 * rounding (see totalExpectedVisitsInMonth).
 */
export function expectedVisitsInMonth(requirement: ExpectationInput, range: MonthRange): number {
  const { from, to } = effectiveRange(requirement.startsOn, requirement.endsOn, range);
  if (from > to) return 0;
  if (requirement.frequency === 'weekly') {
    return plannedVisitsBetween(requirement.weeklyPlans, from, to);
  }

  const activeDays = Math.round((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;
  return (requirement.visitsPerPeriod * activeDays) / periodDays(requirement.frequency, range);
}

/** Rounds an expectation the way the server does: nearest, halves up. */
export function roundExpectedVisits(total: number): number {
  return Math.round(total);
}

/**
 * Month total for a patient's requirements, rounded per career before summing so it
 * matches the sum of that patient's rows in the monthly tracking.
 */
export function totalExpectedVisitsInMonth(
  requirements: Array<ExpectationInput & { career: string }>,
  range: MonthRange,
): number {
  const byCareer = new Map<string, number>();
  requirements.forEach((requirement) => {
    const current = byCareer.get(requirement.career) ?? 0;
    byCareer.set(requirement.career, current + expectedVisitsInMonth(requirement, range));
  });
  return Array.from(byCareer.values())
    .reduce((total, exact) => total + roundExpectedVisits(exact), 0);
}

export const MONTH_NAMES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

export const WEEKDAY_NAMES_ES: Record<number, string> = {
  0: 'Dom',
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
};

export const WEEKDAY_INITIALS_ES: Record<number, string> = {
  0: 'D',
  1: 'L',
  2: 'M',
  3: 'X',
  4: 'J',
  5: 'V',
  6: 'S',
};

// eslint-disable-next-line no-magic-numbers
export const WEEKDAYS_MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0] as const;

const SUNDAY_SORT_TIEBREAKER = 7;

export function weekdaySortKey(day: number): number {
  return day === 0 ? SUNDAY_SORT_TIEBREAKER : day;
}

export const FREQUENCY_LABELS_ES: Record<RequirementFrequency, string> = {
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
};

/** The period a frequency counts visits over, for copy such as "turnos por semana". */
export const PERIOD_NOUNS_ES: Record<RequirementFrequency, string> = {
  weekly: 'semana',
  biweekly: 'quincena',
  monthly: 'mes',
};
